import { useEffect, useMemo, useRef, useState } from "react";

import type {
  RestorePreparationResponse,
  SelectiveRestoreCandidates,
  SelectiveRestorePreview,
  SelectiveRestoreScope,
} from "@board-preserve/contracts";

import {
  confirmCloudSelectiveRestore,
  previewCloudSelectiveRestore,
  readCloudSelectiveRestoreCandidates,
} from "./cloud-connection";
import type { TrelloAuthorizationClient } from "./trello-authorization";

type RequestState = "failed" | "idle" | "loading" | "ready";
type ConfirmationState = "failed" | "idle" | "submitting" | "submitted";

interface SelectiveRestorePanelProps {
  apiUrl: string | null;
  boardName: string;
  client: TrelloAuthorizationClient;
  snapshotId: string;
}

function formatCandidateName(
  candidate: {
    listId?: string;
    name: string;
  },
  listNames: Map<string, string>,
): string {
  return candidate.listId
    ? `${candidate.name} — ${listNames.get(candidate.listId) ?? "captured list"}`
    : candidate.name;
}

export function SelectiveRestorePanel({
  apiUrl,
  boardName,
  client,
  snapshotId,
}: SelectiveRestorePanelProps) {
  const [candidatesState, setCandidatesState] = useState<RequestState>("idle");
  const [candidates, setCandidates] =
    useState<SelectiveRestoreCandidates | null>(null);
  const [scope, setScope] = useState<SelectiveRestoreScope>("card");
  const [selectedSourceId, setSelectedSourceId] = useState("");
  const [previewState, setPreviewState] = useState<RequestState>("idle");
  const [preview, setPreview] = useState<SelectiveRestorePreview | null>(null);
  const [confirmationState, setConfirmationState] =
    useState<ConfirmationState>("idle");
  const [preparation, setPreparation] =
    useState<RestorePreparationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acknowledgements, setAcknowledgements] = useState({
    fidelity: false,
    noOverwrite: false,
    originalBoard: false,
  });
  const idempotencyKey = useRef<string | null>(null);

  const availableCandidates = useMemo(
    () => (scope === "card" ? candidates?.cards : candidates?.lists) ?? [],
    [candidates, scope],
  );
  const listNames = useMemo(
    () => new Map(candidates?.lists.map(({ id, name }) => [id, name]) ?? []),
    [candidates],
  );

  function resetPlan(nextSourceId = selectedSourceId): void {
    setSelectedSourceId(nextSourceId);
    setPreviewState("idle");
    setPreview(null);
    setConfirmationState("idle");
    setPreparation(null);
    setError(null);
    setAcknowledgements({
      fidelity: false,
      noOverwrite: false,
      originalBoard: false,
    });
    idempotencyKey.current = null;
  }

  async function loadCandidates(): Promise<void> {
    setCandidatesState("loading");
    setError(null);
    try {
      const session = await client.createCloudContextSession("restore-preview");
      const result = await readCloudSelectiveRestoreCandidates(
        apiUrl,
        session,
        snapshotId,
      );
      setCandidates(result);
      const initialScope: SelectiveRestoreScope =
        result.cards.length > 0 ? "card" : "list";
      setScope(initialScope);
      resetPlan(
        initialScope === "card"
          ? (result.cards[0]?.id ?? "")
          : (result.lists[0]?.id ?? ""),
      );
      setCandidatesState("ready");
    } catch (candidateError) {
      setError(
        candidateError instanceof Error
          ? candidateError.message
          : "Captured lists and cards could not be loaded.",
      );
      setCandidatesState("failed");
    }
  }

  useEffect(() => {
    void loadCandidates();
    // The panel is keyed by snapshot ID in its parent, so this runs once for
    // each explicitly selected recovery point.
  }, [snapshotId]);

  async function preparePreview(): Promise<void> {
    if (!selectedSourceId) {
      return;
    }
    setPreviewState("loading");
    setError(null);
    setPreparation(null);
    setConfirmationState("idle");
    try {
      const session = await client.createCloudContextSession("restore-preview");
      const result = await previewCloudSelectiveRestore(
        apiUrl,
        session,
        snapshotId,
        scope,
        selectedSourceId,
      );
      setPreview(result);
      setPreviewState("ready");
    } catch (previewError) {
      setError(
        previewError instanceof Error
          ? previewError.message
          : "The selected item could not be checked safely.",
      );
      setPreviewState("failed");
    }
  }

  async function confirmRestore(): Promise<void> {
    if (!preview || !Object.values(acknowledgements).every(Boolean)) {
      return;
    }
    setConfirmationState("submitting");
    setError(null);
    idempotencyKey.current ??= crypto.randomUUID();
    try {
      const session = await client.createCloudContextSession("restore-confirm");
      const result = await confirmCloudSelectiveRestore(
        apiUrl,
        session,
        preview,
        idempotencyKey.current,
      );
      setPreparation(result);
      setConfirmationState("submitted");
    } catch (confirmationError) {
      setError(
        confirmationError instanceof Error
          ? confirmationError.message
          : "The selected restore could not be confirmed safely.",
      );
      setConfirmationState("failed");
    }
  }

  return (
    <section
      aria-labelledby="selective-restore-title"
      className="selective-restore-panel"
    >
      <div className="selective-restore-heading">
        <div>
          <h4 id="selective-restore-title">Choose the missing item</h4>
          <p>
            Board Preserve checks the backup against this board before it
            recreates anything. Existing cards and lists stay unchanged.
          </p>
        </div>
        {candidatesState === "failed" ? (
          <button onClick={() => void loadCandidates()} type="button">
            Try again
          </button>
        ) : null}
      </div>

      {candidatesState === "loading" ? (
        <p aria-live="polite" className="selective-restore-status">
          Checking the selected backup against your live administrator access…
        </p>
      ) : null}

      {candidatesState === "ready" && candidates ? (
        <div className="selective-restore-workflow">
          {candidates.cards.length === 0 && candidates.lists.length === 0 ? (
            <p className="history-empty">
              This backup does not contain a captured list or card to select.
            </p>
          ) : (
            <>
              <fieldset className="selective-scope">
                <legend>Item type</legend>
                <label>
                  <input
                    checked={scope === "card"}
                    disabled={candidates.cards.length === 0}
                    name="selective-restore-scope"
                    onChange={() => {
                      setScope("card");
                      resetPlan(candidates.cards[0]?.id ?? "");
                    }}
                    type="radio"
                  />
                  Card <span>{candidates.cards.length}</span>
                </label>
                <label>
                  <input
                    checked={scope === "list"}
                    disabled={candidates.lists.length === 0}
                    name="selective-restore-scope"
                    onChange={() => {
                      setScope("list");
                      resetPlan(candidates.lists[0]?.id ?? "");
                    }}
                    type="radio"
                  />
                  List <span>{candidates.lists.length}</span>
                </label>
              </fieldset>

              <div className="selective-item-picker">
                <label htmlFor="selective-source-item">Captured {scope}</label>
                <select
                  aria-describedby="selective-source-help"
                  id="selective-source-item"
                  onChange={(event) => resetPlan(event.target.value)}
                  value={selectedSourceId}
                >
                  {availableCandidates.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {formatCandidateName(candidate, listNames)}
                    </option>
                  ))}
                </select>
                <p id="selective-source-help">
                  Board Preserve verifies that this captured ID is absent before
                  preparing a create-only restore.
                </p>
                <button
                  disabled={!selectedSourceId || previewState === "loading"}
                  onClick={() => void preparePreview()}
                  type="button"
                >
                  {previewState === "loading"
                    ? "Checking item…"
                    : "Preview selected item"}
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}

      {error ? (
        <p className="restore-preview-error" role="alert">
          {error}
        </p>
      ) : null}

      {preview ? (
        <div className="selective-preview" aria-live="polite">
          <div className="selective-preview-summary">
            <strong>
              {preview.selection.name} · {preview.totals.planned} planned
              operations
            </strong>
            <span>0 Trello writes during this preview</span>
          </div>
          <ul className="selective-preview-counts">
            {preview.steps
              .filter(({ count }) => count > 0)
              .map((step) => (
                <li key={step.type}>
                  <span>{step.count}</span> {step.type.replaceAll("_", " ")}
                  {step.fidelity === "best_effort" ? " · best effort" : ""}
                </li>
              ))}
          </ul>
          <div className="restore-fidelity-summary">
            <strong>Restore limits</strong>
            <ul>
              <li>
                The missing item is created on <b>{boardName}</b>; existing
                items are never replaced, merged, or deleted.
              </li>
              <li>
                Comments and activity become labelled notes with the original
                author and date.
              </li>
              <li>Some provider photos and covers cannot be recreated.</li>
            </ul>
          </div>
          <details className="restore-fidelity-details">
            <summary>
              Review {preview.warnings.length} exact restore limit
              {preview.warnings.length === 1 ? "" : "s"}
            </summary>
            {preview.warnings.length > 0 ? (
              <ul>
                {preview.warnings.map((warning) => (
                  <li key={warning.code}>
                    <span>{warning.count}</span> {warning.message}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No additional limitations were identified for this item.</p>
            )}
          </details>
          <div className="restore-confirmation-checks">
            <label>
              <input
                checked={acknowledgements.originalBoard}
                onChange={(event) =>
                  setAcknowledgements((current) => ({
                    ...current,
                    originalBoard: event.target.checked,
                  }))
                }
                type="checkbox"
              />
              Restore only into the exact original board, {boardName}.
            </label>
            <label>
              <input
                checked={acknowledgements.noOverwrite}
                onChange={(event) =>
                  setAcknowledgements((current) => ({
                    ...current,
                    noOverwrite: event.target.checked,
                  }))
                }
                type="checkbox"
              />
              Create the missing item only; never overwrite, merge, or delete
              Trello content.
            </label>
            <label>
              <input
                checked={acknowledgements.fidelity}
                onChange={(event) =>
                  setAcknowledgements((current) => ({
                    ...current,
                    fidelity: event.target.checked,
                  }))
                }
                type="checkbox"
              />
              I reviewed the historical-activity and cover limitations above.
            </label>
            <button
              disabled={
                confirmationState === "submitting" ||
                confirmationState === "submitted" ||
                !Object.values(acknowledgements).every(Boolean)
              }
              onClick={() => void confirmRestore()}
              type="button"
            >
              {confirmationState === "submitting"
                ? "Confirming…"
                : confirmationState === "submitted"
                  ? "Restore confirmed"
                  : "Confirm selected restore"}
            </button>
          </div>
          {preparation ? (
            <p className="restore-confirmation-status" role="status">
              {preparation.dispatchStatus === "queued"
                ? "Selective restore queued. Follow its object-by-object progress in Restore reports."
                : "The create-only plan was prepared safely. Selective execution is not enabled yet, so no Trello changes were made."}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
